import React, { useCallback, useState } from 'react';
import { SetupLayout } from '../../components/layout/SetupLayout';
import { useFetchResources, useFetchOrganizations, useMigrationConflicts, useFetchJobs } from '../../hooks/api';
import { Tooltip } from '../../components/Tooltip';
import { useAuth } from '../../hooks/auth';
import { useResourceSearch } from '../../hooks/ui';
import { useNavigate, useLocation } from 'react-router-dom';
import { logger } from '../../utils/logger';
import { isOrgSyncSchedulingEnabled } from '../../utils/appEnvironment';
import {
  useSnapshotName,
  useModalState,
  useOutsideClick,
  useMigrationSetupGuidanceSeen,
  useSmartBack
} from '../../hooks/ui';
import { useResourceFilters, useResourceSelection } from '../../hooks/resource';
import generateDefaultSnapshotName from '../../utils/snapshot';
import { Modal } from '../../components/Modals/Shared/Modal';
import { ViewModalContent } from '../../components/Modals/Shared/ViewModalContent';
import { AttentionModalContent } from '../../components/Modals/Shared/AttentionModalContent';
import Spinner from '../../components/migratorDashboard/Spinner';
import { RepliCloudFAQsModal } from '../../components/Modals/FAQ/RepliCloudFAQsModal';
import OrganizationSelector from '../../components/organization/OrganizationSelector';
import DivisionToOrgSelector, { type MigrationMode } from '../../components/organization/DivisionToOrgSelector';
import ResourceSelector from '../../components/resource/ResourceSelector';
import SelectedResourcesTableHeader from '../../components/resource/table/SelectedResourcesTableHeader';
import SelectedResourcesTableBody from '../../components/resource/table/SelectedResourcesTableBody';
import SelectedResourcesTableButtons from '../../components/resource/table/SelectedResourcesTableButtons';
import createMigration from '../../services/Migration/createMigrationService';
import updateMigration from '../../services/Migration/updateMigrationService';
import { MigrationStatusEnum as MigrationStatus, type Migration, type Resource } from '../../types';
import { mapApiTypeToDisplayName } from '../../utils/resourceTypeMapper';
import { buildMigrationResourceStrings } from '../../utils/buildMigrationResourceStrings';
import { RESOURCE_TYPES, D2D_VISIBLE_RESOURCE_TYPES, DIVISION_SCOPED_RESOURCE_TYPES, DIVISION_ORG_UNIQUE_RESOURCE_TYPES } from '../../utils/resourceTypes';
import { clearOrgCache } from '../../utils/resourceCache';
import { formatRegion } from '../../utils/formatters';
import { ui } from '../../config/uiSizes';
import ScheduleSection from '../../components/Schedule/ScheduleSection';
import RecurringMigrationConflictBanner from '../../components/Migrations/RecurringMigrationConflictBanner';
import type { Schedule } from '../../components/Schedule/SchedulePicker';
import ScheduleToCronExpression from '../../components/Schedule/ScheduleToCronExpression';
import { getSchedule, getScheduleDisplay } from '../../utils/cron';
import { useToast } from '../../components/Toast/ToastProvider';

const MigrationSetup: React.FC = () => {
  const navigate = useNavigate();
  const smartBack = useSmartBack();
  const location = useLocation();
  const { userInfo, accessToken } = useAuth();
  const { showToast } = useToast();
  const [migrationName, setMigrationName] = React.useState('');
  const { snapshotName, setSnapshotName, setSnapshotNameEdited } = useSnapshotName(migrationName);
  const [sourceOrganization, setSourceOrganization] = React.useState('');
  const [targetOrganization, setTargetOrganization] = React.useState('');
  const { organizations, fetchOrganizations } = useFetchOrganizations();
  const {
    resources,
    fetchedResourcesByType: _fetchedResourcesByType, // Dictionary of resources by type (available for future UI enhancements)
    loading: loadingResources,
    error: resourceFetchError,
    fetchProgress,
    fetchedTypes,
    fetchMultipleResourceTypes,
    clearResources,
    typeLoadOrder,
    failedTypes,
    removeFailedTypes
  } = useFetchResources();
  const [resourceType, setResourceType] = React.useState('');
  const [selectedResourceTypes, setSelectedResourceTypes] = React.useState<string[]>([]);
  const [resourceSearch, setResourceSearch] = React.useState('');
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = React.useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const [confirmScope, setConfirmScope] = React.useState<'all' | 'current'>('all');
  const [validationError, setValidationError] = React.useState<string[]>([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = React.useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmLoading, setConfirmLoading] = React.useState(false);
  const [isFullSnapshotMode, setIsFullSnapshotMode] = React.useState(false);
  const [isPrePopulatedForm, setIsPrePopulatedForm] = React.useState(false);
  const [prePopulatedFormId, setPrePopulatedFormId] = React.useState('');
  // Recurring Org Sync scheduling — GA across environments (see isOrgSyncSchedulingEnabled).
  const migrationSchedulingEnabled = isOrgSyncSchedulingEnabled();
  const [migrationMode, setMigrationMode] = React.useState<MigrationMode>('orgToOrg');
  const [sourceDivisionId, setSourceDivisionId] = React.useState('');
  const [sourceDivisionName, setSourceDivisionName] = React.useState('');
  const [targetDivisionId, setTargetDivisionId] = React.useState('');
  const [targetDivisionName, setTargetDivisionName] = React.useState('');
  // Tracks whether the page is loading data for the Edit Migration Settings prefill flow.
  // True from the moment migrationData is detected until resource selections are applied.
  const [isEditMigrationPrefillLoading, setIsEditMigrationPrefillLoading] = React.useState(false);
  const [typeSearch, setTypeSearch] = React.useState('');
  const [nameSearch, setNameSearch] = React.useState('');
  const [includeDependencies, setIncludeDependencies] = React.useState(true);
  const { modalState, handleRunMigrationClick, handleModalClose } = useModalState();
  const [showFAQs, setShowFAQs] = useState(false);
  // Per-user first-time guidance tracking.
  // hasSeen = read from localStorage on mount (determines this session's initial state).
  // showGuidanceBanner = local session state, starts true if this is their first visit.
  const { hasSeen: guidanceHasSeen, markAsSeen: markGuidanceAsSeen } =
    useMigrationSetupGuidanceSeen(userInfo?.userEmail ?? null);
  const [showGuidanceBanner, setShowGuidanceBanner] = useState(!guidanceHasSeen);

  // Migration schedule state (recurring migrations feature)
  const [isRecurringEnabled, setIsRecurringEnabled] = React.useState(false);
  const [migrationSchedule, setMigrationSchedule] = React.useState<Schedule>({
    days: [],
    hour: 7,
    minute: 0,
    ampm: 'AM'
  });
  const migrationCronExpression = ScheduleToCronExpression({
    ...migrationSchedule
  });
  const migrationScheduleDisplay = getScheduleDisplay(migrationCronExpression);

  const getOrganizationKey = useCallback((org: { name: string; shortName?: string }): string => {
    return org.shortName ?? org.name;
  }, []);

  const findOrganization = useCallback(
    (value: string) =>
      organizations.find(
        (org) => org.name === value || org.shortName === value
      ),
    [organizations]
  );

  // The org a recurring sync targets: for org-to-org it's the selected target org; for
  // division-to-division the target lives in the *same* org as the source, so the conflict
  // check keys off the source org. Recurring D2D executes just like recurring O2O now, so it
  // must participate in the same "another recurring sync already targets this org" detection.
  const conflictTargetOrgName =
    migrationMode === 'divisionToDivision' ? sourceOrganization : targetOrganization;
  const conflictCheckEnabled = isRecurringEnabled && !!conflictTargetOrgName;

  const conflictCheck = useMigrationConflicts({
    targetOrgId: findOrganization(conflictTargetOrgName)?.id ?? '',
    ...(prePopulatedFormId ? { excludeMigrationId: prePopulatedFormId } : {}),
    enabled: conflictCheckEnabled,
  });

  // In-page details modal for a conflicting recurring migration. Lazily loads the
  // full migration record (with mapped status) so the modal can mirror the dashboard:
  // failed / partially-completed → Attention content, otherwise → View content.
  const {
    jobs: conflictMigrationDetails,
    loading: conflictDetailsLoading,
    fetchJobs: loadConflictMigrationDetails,
  } = useFetchJobs({ enablePolling: false });
  const [conflictDetailsId, setConflictDetailsId] = React.useState<string | null>(null);

  const handleViewConflictDetails = useCallback(
    (migrationId: string) => {
      setConflictDetailsId(migrationId);
      loadConflictMigrationDetails();
    },
    [loadConflictMigrationDetails]
  );

  const conflictDetailsJob = conflictDetailsId
    ? conflictMigrationDetails.find((j) => j.id === conflictDetailsId) ?? null
    : null;
  const conflictDetailsVariant: 'view' | 'attention' =
    conflictDetailsJob &&
      (String(conflictDetailsJob.status).toLowerCase().includes('failed') ||
        (!conflictDetailsJob.isSnapshot && String(conflictDetailsJob.status) === 'PartiallyCompleted'))
      ? 'attention'
      : 'view';

  // Log page mount for traceability
  React.useEffect(() => {
    logger.info('Page: MigrationSetup mounted');
  }, []);

  // On first visit, immediately write to localStorage so next login it won't show again.
  // The banner still stays visible this session until the user clicks X.
  React.useEffect(() => {
    if (!guidanceHasSeen) {
      markGuidanceAsSeen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);
  const dropdownButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const selectedResourceTitle = 'Selected Resources to Migrate';
  const panelRef = React.useRef<HTMLDivElement | null>(null);

  // Division-to-Division only shows resources that live in the selected SOURCE division.
  // The backend returns each resource's division name (Resource.division); resources with
  // no division are org-scoped. Fail open per type: if the backend returns no division
  // metadata for a type at all, keep those resources rather than hiding the entire type.
  const selectableResources = React.useMemo(() => {
    if (migrationMode !== 'divisionToDivision' || !sourceDivisionName) {
      return resources;
    }
    const target = sourceDivisionName.trim().toLowerCase();
    const byType = new Map<string, Resource[]>();
    for (const r of resources) {
      byType.set(r.type, [...(byType.get(r.type) ?? []), r]);
    }
    const result: Resource[] = [];
    for (const items of byType.values()) {
      const anyHasDivision = items.some((r) => (r.division ?? '').trim() !== '');
      if (!anyHasDivision) {
        result.push(...items);
      } else {
        result.push(...items.filter((r) => (r.division ?? '').trim().toLowerCase() === target));
      }
    }
    return result;
  }, [resources, migrationMode, sourceDivisionName]);

  const { searchedResources } = useResourceFilters(
    selectableResources,
    selectedResourceTypes,
    resourceSearch,
    typeLoadOrder,
    failedTypes
  );
  const {
    selectedResourceIds,
    setSelectedResourceIds,
    handleResourceToggle,
    handleSelectAllToggle,
    handleResetSelections,
    allSelectedResources,
    resourceTypeCounts
  } = useResourceSelection(resources, resourceType);

  // Pre-populate form from failed migration data
  React.useEffect(() => {
    const locationState = location.state as {
      job?: Migration;
      migrationData?: {
        name: string;
        sourceOrgName: string;
        targetOrgName: string;
        sourceOrgId: string;
        targetOrgId: string;
        resources: string[];
        id: string;
        isPrePopulatedForm: boolean;
      };
    } | null;
    const migrationData = locationState?.migrationData;

    if (migrationData) {
      setIsEditMigrationPrefillLoading(true);
      // Set basic fields
      setMigrationName(migrationData.name);
      setSnapshotName(migrationData.name);
      setSourceOrganization(migrationData.sourceOrgName);
      setTargetOrganization(migrationData.targetOrgName);
      setIsPrePopulatedForm(true);
      setPrePopulatedFormId(migrationData.id);

      // Restore the saved dependency-inclusion choice so editing/rerunning a migration
      // that opted out does not silently flip back to the default.
      if (typeof locationState?.job?.includeDependencies === 'boolean') {
        setIncludeDependencies(locationState.job.includeDependencies);
      }

      // Handle recurring migration schedule if present
      if (locationState?.job?.snapshotCronExpression) {
        setIsRecurringEnabled(true);
        const schedule = getSchedule(locationState.job.snapshotCronExpression);
        if (schedule) {
          setMigrationSchedule(schedule);
        }
      }

      // Parse resources from "type::id" format
      const resourcesByType: Record<string, string[]> = {};
      const resourceTypes = new Set<string>();

      migrationData.resources.forEach((resource) => {
        const [apiType, id] = resource.split('::');

        // Skip if apiType or id is missing
        if (!apiType || !id) return;

        // Convert API type to display name (e.g., 'architect_datatable' -> 'Architect Datatable')
        const displayType = mapApiTypeToDisplayName(apiType);

        resourceTypes.add(displayType);
        if (!resourcesByType[displayType]) {
          resourcesByType[displayType] = [];
        }
        resourcesByType[displayType].push(id);
      });

      // Set selected resource types
      const typesToFetch = Array.from(resourceTypes);
      setSelectedResourceTypes(typesToFetch);

      // Store resource selection data to apply after resources are fetched
      // This will be used by a separate effect that watches for resources to load
      sessionStorage.setItem('pendingResourceSelection', JSON.stringify(resourcesByType));

      // Automatically fetch resources for pre-populated migration.
      // Use migrationData.sourceOrgName directly to avoid setState timing issues.
      if (typesToFetch.length > 0 && migrationData.sourceOrgName) {
        fetchMultipleResourceTypes(typesToFetch, migrationData.sourceOrgName);
        prevFetchedTypesRef.current = typesToFetch;
      } else {
        // No resources to fetch; field population is already complete
        setIsEditMigrationPrefillLoading(false);
      }

      // Clear location state so refresh doesn't re-populate
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, setSnapshotName, fetchMultipleResourceTypes]);

  // Apply resource selection after resources are fetched
  React.useEffect(() => {
    const pendingSelection = sessionStorage.getItem('pendingResourceSelection');

    if (pendingSelection && resources.length > 0) {
      try {
        const resourcesByType: Record<string, string[]> = JSON.parse(pendingSelection);

        // Find and select matching resources
        const idsToSelect: string[] = [];
        resources.forEach((resource) => {
          const expectedIds = resourcesByType[resource.type];
          if (expectedIds) {
            const isMatch =
              expectedIds.includes(resource.id) ||
              expectedIds.includes('*') ||
              expectedIds.includes('**');
            if (isMatch) {
              idsToSelect.push(resource.id);
            }
          }
        });

        if (idsToSelect.length > 0) {
          setSelectedResourceIds(idsToSelect);
          sessionStorage.removeItem('pendingResourceSelection');
        } else {
          // Resources loaded but none matched; prefill is as complete as it can be
          sessionStorage.removeItem('pendingResourceSelection');
        }
        setIsEditMigrationPrefillLoading(false);
      } catch (err) {
        logger.error('Failed to parse pending resource selection', { error: err });
        sessionStorage.removeItem('pendingResourceSelection');
        setIsEditMigrationPrefillLoading(false);
      }
    }
  }, [resources, setSelectedResourceIds]);

  // Remove selected resource IDs when their types are unchecked
  // But preserve selections when new types are added
  const prevSelectedTypesRef = React.useRef<string[]>([]);
  React.useEffect(() => {
    const prevTypes = prevSelectedTypesRef.current;

    if (selectedResourceTypes.length === 0 && prevTypes.length > 0) {
      // If no types selected anymore, clear all selections
      setSelectedResourceIds([]);
    } else if (prevTypes.length > selectedResourceTypes.length) {
      // Types were removed - filter out resource IDs belonging to removed types
      const removedTypes = prevTypes.filter((t) => !selectedResourceTypes.includes(t));
      if (removedTypes.length > 0) {
        // Use allSelectedResources to check types, not just current resources array
        setSelectedResourceIds((prevIds) => {
          return prevIds.filter((id) => {
            const resource = allSelectedResources.find((r) => r.id === id);
            return resource && !removedTypes.includes(resource.type);
          });
        });
      }
    }

    // When new types are added (length increases), selections are preserved automatically
    prevSelectedTypesRef.current = selectedResourceTypes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResourceTypes]);

  // Fetch organizations on mount
  React.useEffect(() => {
    if (accessToken) {
      fetchOrganizations();
    }
  }, [accessToken, fetchOrganizations]);

  // Clear cache when source organization changes to get fresh data
  const prevSourceOrgRef = React.useRef<string>('');
  React.useEffect(() => {
    if (
      sourceOrganization &&
      prevSourceOrgRef.current &&
      prevSourceOrgRef.current !== sourceOrganization
    ) {
      logger.info('Source organization changed in MigrationSetup, clearing cache and resource selections', {
        old: prevSourceOrgRef.current,
        new: sourceOrganization,
        role: 'source'
      });
      clearOrgCache(prevSourceOrgRef.current);
      setResourceType('');
      setSelectedResourceTypes([]);
      clearResources();
      handleResetSelections('all');
    }
    prevSourceOrgRef.current = sourceOrganization;
  }, [sourceOrganization, clearResources, handleResetSelections]);

  // D2D: when the source division changes, previously selected resources belong to the
  // old division. Clear the type dropdown, selected types, and fetched resources so the
  // available list and the selected table reflect the new source division.
  const prevSourceDivisionIdRef = React.useRef<string>('');
  React.useEffect(() => {
    if (migrationMode !== 'divisionToDivision') {
      prevSourceDivisionIdRef.current = sourceDivisionId;
      return;
    }
    const prev = prevSourceDivisionIdRef.current;
    if (prev && prev !== sourceDivisionId) {
      logger.info('Source division changed in MigrationSetup, clearing resource type and selections', {
        old: prev,
        new: sourceDivisionId
      });
      setResourceType('');
      setSelectedResourceTypes([]);
      clearResources();
      handleResetSelections('all');
    }
    prevSourceDivisionIdRef.current = sourceDivisionId;
  }, [sourceDivisionId, migrationMode, clearResources, handleResetSelections]);

  // Log target organization changes
  const prevTargetOrgRef = React.useRef<string>('');
  React.useEffect(() => {
    if (
      targetOrganization &&
      prevTargetOrgRef.current &&
      prevTargetOrgRef.current !== targetOrganization
    ) {
      logger.info('Target organization changed in MigrationSetup', {
        old: prevTargetOrgRef.current,
        new: targetOrganization,
        role: 'target'
      });
    }
    prevTargetOrgRef.current = targetOrganization;
  }, [targetOrganization]);

  // Log resource type changes
  const prevResourceTypeRef = React.useRef<string>('');
  React.useEffect(() => {
    if (
      resourceType &&
      prevResourceTypeRef.current &&
      prevResourceTypeRef.current !== resourceType
    ) {
      logger.info('Resource type changed in MigrationSetup', {
        from: prevResourceTypeRef.current,
        to: resourceType,
        selectedCount: selectedResourceIds.length
      });
    }
    prevResourceTypeRef.current = resourceType;
  }, [resourceType, selectedResourceIds.length]);

  // When full snapshot mode is active, display all resource types in the table
  const displayedResources = React.useMemo(() => {
    if (isFullSnapshotMode) {
      return RESOURCE_TYPES.map((type) => ({
        id: `all-${type}`,
        name: '* (All)',
        type: type
      }));
    }
    return allSelectedResources;
  }, [isFullSnapshotMode, allSelectedResources]);

  // Track fetched types to determine if we have pending unfetched types
  const prevFetchedTypesRef = React.useRef<string[]>([]);

  // Determine if there are pending types that haven't been fetched
  const hasPendingTypes = React.useMemo(() => {
    if (selectedResourceTypes.length === 0) return false;
    const prevFetched = prevFetchedTypesRef.current;
    const newTypes = selectedResourceTypes.filter((t) => !prevFetched.includes(t));
    return newTypes.length > 0;
  }, [selectedResourceTypes]);

  // Manual fetch handler
  const handleFetchResources = React.useCallback(() => {
    if (!sourceOrganization || !accessToken) return;

    const prevFetched = prevFetchedTypesRef.current;
    const newTypes = selectedResourceTypes.filter((t) => !prevFetched.includes(t));

    if (newTypes.length > 0) {
      fetchMultipleResourceTypes(newTypes, sourceOrganization);
      prevFetchedTypesRef.current = selectedResourceTypes;
    }
  }, [sourceOrganization, selectedResourceTypes, accessToken, fetchMultipleResourceTypes]);

  // Clear resources when types are removed
  React.useEffect(() => {
    const prevFetched = prevFetchedTypesRef.current;

    if (selectedResourceTypes.length === 0 && prevFetched.length > 0) {
      clearResources();
      prevFetchedTypesRef.current = [];
    } else if (prevFetched.length > selectedResourceTypes.length) {
      // Types were removed - update the ref to only include currently selected types
      const removedTypes = prevFetched.filter((t) => !selectedResourceTypes.includes(t));
      prevFetchedTypesRef.current = prevFetched.filter((t) => selectedResourceTypes.includes(t));
      // Remove failed types for types that were deselected
      if (removedTypes.length > 0) {
        removeFailedTypes(removedTypes);
      }
    }
    // Note: We don't automatically fetch when types are added - user must click "Load Resources"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResourceTypes]);

  // Close dropdown when resourceType changes (so UI resets when switching)
  React.useEffect(() => setResourcesDropdownOpen(false), [resourceType]);

  // Close dropdown when resources start loading
  React.useEffect(() => {
    if (loadingResources) {
      setResourcesDropdownOpen(false);
    }
  }, [loadingResources]);

  // Close dropdown when clicking outside
  useOutsideClick([dropdownButtonRef, panelRef], () => setResourcesDropdownOpen(false));

  // Clear search when dropdown closes
  React.useEffect(() => {
    if (!resourcesDropdownOpen) setResourceSearch('');
  }, [resourcesDropdownOpen]);

  const openConfirmModal = React.useCallback((scope: 'all' | 'current') => {
    setConfirmScope(scope);
    setConfirmModalOpen(true);
  }, []);

  const totalSelectedCount = displayedResources.length;

  const isFormValid = React.useMemo(() => {
    const targetValid = migrationMode === 'divisionToDivision'
      ? !!(sourceDivisionId && targetDivisionId)
      : !!targetOrganization;
    const basicValid = !!(
      migrationName.trim() &&
      migrationName.trim().length >= 2 &&
      sourceOrganization &&
      targetValid &&
      (totalSelectedCount > 0 || isFullSnapshotMode)
    );

    if (!basicValid) return false;

    // Check recurring migration validation
    if (isRecurringEnabled && migrationSchedule.days.length === 0) {
      return false;
    }

    return true;
  }, [
    migrationName,
    sourceOrganization,
    targetOrganization,
    totalSelectedCount,
    isFullSnapshotMode,
    migrationMode,
    sourceDivisionId,
    targetDivisionId,
    isRecurringEnabled,
    migrationSchedule.days.length
  ]);

  const isButtonEnabled = isFormValid;

  const validateForm = useCallback(
    (
      name: string,
      sourceOrg: string,
      targetOrg: string,
      count: number,
      fullSnapshot: boolean,
      mode: MigrationMode = 'orgToOrg',
      srcDivId = '',
      tgtDivId = ''
    ): boolean => {
      const errors: string[] = [];
      if (!name.trim()) {
        errors.push('Please enter a Migration Name');
      }
      if (name.trim().length < 2) {
        errors.push('Migration Name must be at least 2 characters');
      }
      if (!sourceOrg) {
        errors.push('Please select a Source Organization');
      }
      if (mode === 'divisionToDivision') {
        if (!srcDivId) {
          errors.push('Please select a source division');
        }
        if (!tgtDivId) {
          errors.push('Please select a target division');
        }
      } else {
        if (!targetOrg) {
          errors.push('Please select a Target Organization');
        }
      }
      if (count === 0 && !fullSnapshot) {
        errors.push('Please select at least one resource to migrate or use Full Export option');
      }
      if (isRecurringEnabled) {
        if (!migrationSchedule.days || migrationSchedule.days.length === 0) {
          errors.push('Please select at least one day or turn off the recurring migration toggle.');
        }
      }
      setValidationError(errors);
      if (errors.length > 0) {
        logger.warn('Migration form validation failed', {
          errors,
          formState: { name, sourceOrg, targetOrg, resourceCount: count, fullSnapshot }
        });
      }
      return errors.length === 0;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  React.useEffect(() => {
    if (validationError.length > 0 && isFormValid) {
      setValidationError([]);
    }
  }, [
    allSelectedResources.length,
    hasAttemptedSubmit,
    totalSelectedCount,
    isFullSnapshotMode,
    validationError,
    isFormValid
  ]);

  React.useEffect(() => {
    if (hasAttemptedSubmit) {
      validateForm(
        migrationName,
        sourceOrganization,
        targetOrganization,
        totalSelectedCount,
        isFullSnapshotMode,
        migrationMode,
        sourceDivisionId,
        targetDivisionId
      );
    }
  }, [
    migrationName,
    sourceOrganization,
    targetOrganization,
    totalSelectedCount,
    isFullSnapshotMode,
    migrationMode,
    sourceDivisionId,
    targetDivisionId,
    hasAttemptedSubmit,
    validateForm
  ]);

  const { filteredResourcesByType } = useResourceSearch(
    allSelectedResources,
    typeSearch,
    nameSearch
  );

  // Filter resource types when in full snapshot mode
  const filteredResourceTypes = React.useMemo(() => {
    // In D2D full export, only show division-aware types
    const baseTypes = (migrationMode === 'divisionToDivision' && isFullSnapshotMode)
      ? D2D_VISIBLE_RESOURCE_TYPES
      : RESOURCE_TYPES;
    if (!typeSearch.trim()) return baseTypes;
    const searchLower = typeSearch.trim().toLowerCase();
    return baseTypes.filter((type) => type.toLowerCase().includes(searchLower));
  }, [typeSearch, migrationMode, isFullSnapshotMode]);

  const tableScrollClass = `h-72 overflow-y-auto p-3`;

  // Handle selecting all resource types with wildcard
  const handleSelectAllResourceTypes = React.useCallback(() => {
    if (!sourceOrganization) {
      setValidationError(['Please select a Source Organization first']);
      return;
    }

    // Clear any manual selections and enable full snapshot mode
    handleResetSelections('all');
    setIsFullSnapshotMode(true);

    const isD2DMode = migrationMode === 'divisionToDivision';
    logger.info('Enabled full Export mode - all resource types will be included', {
      count: isD2DMode ? D2D_VISIBLE_RESOURCE_TYPES.length : RESOURCE_TYPES.length,
      scope: isD2DMode ? 'D2D division-aware types only' : 'all types'
    });
  }, [sourceOrganization, handleResetSelections, setValidationError, migrationMode]);

  // Calculate total counts for each resource type for display in the table header badges
  const totalCountForType = React.useMemo(
    () =>
      RESOURCE_TYPES.map((displayType) => {
        const totalCountForType = selectableResources.filter((r) => r.type === displayType).length;
        return { displayType, totalCountForType };
      }),
    [selectableResources]
  );

  const totalCountForAllTypes = React.useMemo(
    () =>
      totalCountForType.length > 0
        ? new Map(
          totalCountForType.map(({ displayType, totalCountForType }) => [
            displayType,
            totalCountForType
          ])
        )
        : resourceTypeCounts,
    [totalCountForType, resourceTypeCounts]
  );

  // D2D only: selected types that were loaded but have zero resources in the source division. The
  // type list is division-eligible (not division-derived), so an operator can select a type the
  // division doesn't contain; this surfaces those so they can be dropped from the selection.
  const emptyDivisionTypes = React.useMemo(() => {
    if (migrationMode !== 'divisionToDivision' || !sourceDivisionName || loadingResources) return [];
    return selectedResourceTypes.filter(
      (t) => fetchedTypes.has(t) && (totalCountForAllTypes.get(t) ?? 0) === 0
    );
  }, [migrationMode, sourceDivisionName, loadingResources, selectedResourceTypes, fetchedTypes, totalCountForAllTypes]);

  const handleRemoveEmptyDivisionTypes = React.useCallback(() => {
    setSelectedResourceTypes((prev) => prev.filter((t) => !emptyDivisionTypes.includes(t)));
  }, [emptyDivisionTypes]);

  // Disable full snapshot mode when user manually selects resources
  React.useEffect(() => {
    if (allSelectedResources.length > 0 && isFullSnapshotMode) {
      setIsFullSnapshotMode(false);
    }
  }, [allSelectedResources.length, isFullSnapshotMode]);

  //run confirm migration logic here
  const handleMigrationModeChange = React.useCallback((mode: MigrationMode) => {
    setMigrationMode(mode);
    // Clear resource selections when switching modes to avoid mismatched types
    setSelectedResourceTypes([]);
    handleResetSelections('all');
    setIsFullSnapshotMode(false);
    clearResources();
    prevFetchedTypesRef.current = [];
    if (mode === 'orgToOrg') {
      setSourceDivisionId('');
      setSourceDivisionName('');
      setTargetDivisionId('');
      setTargetDivisionName('');
    }
  }, [clearResources, handleResetSelections]);

  // Post-submit safety net: if the backend reports the target org already has active
  // recurring migrations, surface a non-blocking warning (the live check may have been
  // missed if the user submitted quickly).
  const warnIfRecurringConflict = (response: {
    hasConflict?: boolean;
    conflictingMigrations?: unknown[];
  }) => {
    if (!response.hasConflict) return;
    const count = response.conflictingMigrations?.length ?? 0;
    logger.warn('Migration saved while target org has active recurring migrations', { count });
    showToast(
      `Heads up: this target organization already has ${count} active recurring migration${count === 1 ? '' : 's'}.`,
      'warning'
    );
  };

  const handleRunConfirm = async () => {
    setConfirmLoading(true);
    setError(null);

    try {
      if (!accessToken || !userInfo?.orgId) {
        throw new Error('Missing authentication information');
      }

      // Find org IDs from selected org names
      const sourceOrg = findOrganization(sourceOrganization);
      const targetOrg = migrationMode === 'divisionToDivision'
        ? sourceOrg
        : findOrganization(targetOrganization);

      if (!sourceOrg || !targetOrg) {
        throw new Error('Could not find organization IDs');
      }

      let resourceStrings: string[] = [];
      // Display-only id→name for individually-selected resources (pre-export views show names).
      let resourceDetails: { resourceType: string; resourceId: string; resourceName: string }[] = [];
      let optimisticMigration: Migration;

      if (isFullSnapshotMode) {
        // Full snapshot mode: send empty array to indicate no filtering (includes all current and future resources)
        resourceStrings = [];
      } else {
        // Manual selection mode. In Division-to-Division the picker is scoped to the source division,
        // so an "all of this type selected" state must NOT collapse to a "type::*" wildcard (that
        // would export the type org-wide). buildMigrationResourceStrings suppresses the wildcard when
        // division-scoped and emits explicit "type::id" selectors instead.
        const built = buildMigrationResourceStrings(
          allSelectedResources,
          totalCountForAllTypes,
          migrationMode === 'divisionToDivision'
        );
        resourceStrings = built.resourceStrings;
        resourceDetails = built.resourceDetails;
      }

      if (isPrePopulatedForm) {
        logger.info('Creating export from pre-populated form', {
          snapshotName,
          sourceOrgId: sourceOrg.id,
          sourceOrgName: sourceOrg.name,
          resourceCount: resourceStrings.length
        });

        // Update the existing snapshot request (using same endpoint as migrations, but without target org)
        const response = await updateMigration(accessToken, prePopulatedFormId, {
          name: snapshotName,
          sourceOrgId: sourceOrg.id,
          sourceOrgName: getOrganizationKey(sourceOrg),
          resources: resourceStrings,
          resourceDetails,
          advanceToNextStatus: false,
          status: MigrationStatus.ExportQueued, // Set status back to queued so it can be picked up by the scheduler again
          ...(isRecurringEnabled &&
            migrationCronExpression &&
            migrationCronExpression.length > 0 && { snapshotCronExpression: migrationCronExpression })
        });

        logger.info('Export updated successfully from pre-populated form', {
          migrationId: response.id,
          status: response.status
        });
        warnIfRecurringConflict(response);
        optimisticMigration = {
          id: response.id,
          name: snapshotName,
          customerId: userInfo.orgId,
          createdBy: userInfo.userEmail || userInfo.userName,
          sourceOrgId: sourceOrg.id,
          sourceOrgName: getOrganizationKey(sourceOrg),
          targetOrgId: response.targetOrgId ?? '',
          targetOrgName: response.targetOrgName ?? '',
          resources: resourceStrings,
          exportAll: isFullSnapshotMode,
          includeDependencies,
          isSnapshot: !response.targetOrgId,
          isActive: true,
          status: response.status,
          createdAtUtc: response.createdAtUtc,
          ...(isRecurringEnabled &&
            migrationCronExpression &&
            migrationCronExpression.length > 0 && { snapshotCronExpression: migrationCronExpression })
        };
      } else {
        // Create the migration request
        const response = await createMigration(accessToken, {
          name: migrationName,
          customerId: userInfo.orgId,
          createdBy: userInfo.userEmail || userInfo.userName,
          sourceOrgId: sourceOrg.id,
          sourceOrgName: getOrganizationKey(sourceOrg),
          targetOrgId: targetOrg.id,
          targetOrgName: getOrganizationKey(targetOrg),
          resources: resourceStrings,
          resourceDetails,
          exportAll: isFullSnapshotMode,
          includeDependencies,
          ...(migrationMode === 'divisionToDivision' && {
            sourceDivisionId,
            sourceDivisionName,
            targetDivisionId,
            targetDivisionName,
            migrationType: 1 as const  // backend MigrationType.DivisionToDivision = 1 (string form rejected without global JsonStringEnumConverter)
          }),
          ...(isRecurringEnabled &&
            migrationCronExpression &&
            migrationCronExpression.length > 0 && { snapshotCronExpression: migrationCronExpression })
        });

        logger.info('Migration created successfully', {
          migrationId: response.id,
          status: response.status
        });
        warnIfRecurringConflict(response);
        optimisticMigration = {
          id: response.id,
          name: migrationName,
          customerId: userInfo.orgId,
          createdBy: userInfo.userEmail || userInfo.userName,
          sourceOrgId: sourceOrg.id,
          sourceOrgName: getOrganizationKey(sourceOrg),
          targetOrgId: targetOrg.id,
          targetOrgName: getOrganizationKey(targetOrg),
          resources: resourceStrings,
          exportAll: isFullSnapshotMode,
          includeDependencies,
          isSnapshot: false,
          isActive: true,
          status: response.status,
          createdAtUtc: response.createdAtUtc,
          ...(migrationMode === 'divisionToDivision' && {
            migrationType: 'DivisionToDivision' as const,
            sourceDivisionId,
            sourceDivisionName,
            targetDivisionId,
            targetDivisionName
          }),
          ...(isRecurringEnabled &&
            migrationCronExpression &&
            migrationCronExpression.length > 0 && { snapshotCronExpression: migrationCronExpression })
        };
      }

      handleModalClose();

      // Hand the new record to the dashboard so it appears immediately, then revalidates.
      navigate('/dashboard', {
        state: {
          message: `Migration ${isPrePopulatedForm ? 'updated' : 'created'} for ${optimisticMigration.name}`,
          updatedMigration: optimisticMigration
        }
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create migration';
      logger.error('Failed to create migration', { error: errorMsg });
      setError(
        'An error occurred while creating the migration. Please try again. \nError: ' + errorMsg
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  const leftContent = (
    <>
      {isPrePopulatedForm && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg px-4 py-3 flex items-start gap-3">
          <svg
            className={`${ui.svg.sm} text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          </svg>
          <div>
            <p className={`${ui.text.label} font-semibold text-yellow-800 dark:text-yellow-300`}>
              Redoing migration setup
            </p>
            <p className={`${ui.text.xs} text-yellow-700 dark:text-yellow-400 mt-0.5`}>
              This migration previously failed at the{' '}
              <span className="font-medium">Export</span> stage. Adjust the settings if needed and
              click <span className="font-medium">Next</span> to re-submit using the same migration
              — no new migration will be created.
            </p>
          </div>
        </div>
      )}
      <div>
        <div className="space-y-6">
          <div className={`flex flex-col sm:flex-row items-center ${ui.gap.md} mt-4`}>
            <label
              htmlFor="migrationName"
              className={`w-48 ${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100`}
            >
              Migration Name:
            </label>
            <div className="flex-1 w-full">
              <input
                id="migrationName"
                type="text"
                value={migrationName}
                placeholder="Enter Migration Name"
                onChange={(e) => {
                  setMigrationName(e.target.value);
                }}
                className={`w-full ${ui.input.custom} border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${migrationName.trim() && migrationName.trim().length < 2
                  ? 'border-red-500 dark:border-red-600'
                  : 'border-gray-300 dark:border-gray-700'
                  }`}
              />
              {migrationName.trim() && migrationName.trim().length < 2 && (
                <p className={`mt-1 ${ui.text.xs} text-red-600 dark:text-red-400`}>
                  Migration Name must be at least 2 characters long
                </p>
              )}
            </div>
          </div>

          <div className={`flex flex-col sm:flex-row items-center ${ui.gap.md}`}>
            <OrganizationSelector
              orgVariant="sourceOrganization"
              value={sourceOrganization}
              onChange={(org) => {
                setSourceOrganization(org);
              }}
            />
          </div>
          <DivisionToOrgSelector
            sourceOrganizationKey={getOrganizationKey(findOrganization(sourceOrganization) ?? { name: sourceOrganization })}
            migrationMode={migrationMode}
            onMigrationModeChange={handleMigrationModeChange}
            targetOrganization={targetOrganization}
            onTargetOrganizationChange={setTargetOrganization}
            sourceDivisionId={sourceDivisionId}
            sourceDivisionName={sourceDivisionName}
            targetDivisionId={targetDivisionId}
            targetDivisionName={targetDivisionName}
            onSourceDivisionChange={(id, name) => { setSourceDivisionId(id); setSourceDivisionName(name); }}
            onTargetDivisionChange={(id, name) => { setTargetDivisionId(id); setTargetDivisionName(name); }}
          />
        </div>
      </div>

      <div>
        <h3
          className={`pt-6 border-t-2 border-gray-300 dark:border-gray-600 ${ui.text.formStepTitle} text-gray-800 dark:text-gray-100 ${ui.mb.section}`}
        >
          <span className="font-extrabold">Pre-Migration Export</span>
          <Tooltip
            content={
              <span className="font-semibold">
                A name to identify the exported data from your source organization before the
                migration begins.
              </span>
            }
            position="right"
          />
        </h3>
        <div className={`flex flex-col sm:flex-row items-start ${ui.gap.sm}`}>
          <label
            htmlFor="snapshotName"
            className={`w-48 ${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100`}
          >
            Export Name:
          </label>

          <div className="flex-1">
            <div className="flex items-center gap-2">
              <input
                id="snapshotName"
                value={snapshotName}
                onChange={(e) => {
                  setSnapshotName(e.target.value);
                  setSnapshotNameEdited(true);
                }}
                className={`flex-1 ${ui.input.custom} border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                aria-label="Export name"
              />
              <button
                type="button"
                className="text-sm text-blue-600 dark:text-blue-500 hover:underline"
                onClick={() => {
                  const defaultName = generateDefaultSnapshotName(migrationName);
                  setSnapshotName(defaultName);
                  setSnapshotNameEdited(false);
                }}
                title="Reset to auto-generated snapshot name"
              >
                Reset
              </button>
            </div>

            <p className={`${ui.text.xs} text-gray-500 mt-2`}>
              Auto-filled from Migration Name — but user editable.
            </p>
          </div>
        </div>
      </div>
      <div>
        <div
          className={`flex lg:flex-col xl:flex-row items-center lg:items-start xl:items-center justify-between lg:gap-2 xl:gap-0 pt-6 border-t-2 border-gray-300 dark:border-gray-600 ${ui.mb.section}`}
        >
          <div className={`flex items-center ${ui.gap.sm}`}>
            <h3 className="text-lg md:text-xl font-extrabold text-gray-800 dark:text-gray-100">
              Select Resources to Migrate
            </h3>
            <Tooltip
              content={
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      Manual Selection
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      Select one or more resource types, then click "Load Resources" to fetch
                      resources. Then pick individual resources from the resources dropdown or
                      select all for each type. You can add or remove types and resources at any
                      time—your selections will be saved and seen in the table to the right. All
                      dependent Genesys objects tied to the selected resources will be automatically
                      included in the migration and may be excluded during the transformation step.
                    </p>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
                    <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      Full Export
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      Click "Select All Resources (Full Export)" to automatically include all
                      resource types with wildcard selection (*) - no need to pick individual types
                      or resources.
                    </p>
                  </div>
                </div>
              }
              position="right"
              className={ui.text.formSectionLabel}
            />
          </div>
          <button
            type="button"
            onClick={
              isFullSnapshotMode ? () => setIsFullSnapshotMode(false) : handleSelectAllResourceTypes
            }
            disabled={!sourceOrganization}
            className={`whitespace-nowrap shrink-0 lg:self-end xl:self-auto ${ui.button.md} rounded-lg font-semibold shadow transition ${sourceOrganization
              ? isFullSnapshotMode
                ? 'bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-600 cursor-pointer'
                : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white cursor-pointer'
              : 'bg-gray-400 dark:bg-gray-600 text-gray-200 cursor-not-allowed opacity-60'
              }`}
            title={
              !sourceOrganization
                ? 'Please select a Source Organization first'
                : 'Select all resource types for full export'
            }
          >
            {isFullSnapshotMode ? (
              <>
                <span className="lg:hidden">Exit Full Export</span>
                <span className="hidden lg:inline">Exit Full Export</span>
              </>
            ) : (
              <>
                <span className="lg:hidden">Select All Resources (Full Export)</span>
                <span className="hidden lg:inline">Full Export</span>
              </>
            )}
          </button>
        </div>

        <ResourceSelector
          resourceType={resourceType}
          onResourceTypeChange={setResourceType}
          selectedResourceTypes={selectedResourceTypes}
          onSelectedResourceTypesChange={setSelectedResourceTypes}
          resourceSearch={resourceSearch}
          onResourceSearchChange={setResourceSearch}
          resourcesDropdownOpen={resourcesDropdownOpen}
          setResourcesDropdownOpen={setResourcesDropdownOpen}
          uniqueResourceTypes={
            migrationMode === 'divisionToDivision'
              ? D2D_VISIBLE_RESOURCE_TYPES
              : RESOURCE_TYPES
          }
          loadingResources={loadingResources}
          fetchProgress={fetchProgress}
          fetchError={resourceFetchError}
          organizationSelected={
            !!sourceOrganization &&
            (migrationMode === 'divisionToDivision'
              ? !!(sourceDivisionId && targetDivisionId)
              : !!targetOrganization)
          }
          searchedResources={searchedResources}
          selectedResourceIds={selectedResourceIds}
          onToggleResource={handleResourceToggle}
          onSelectAll={handleSelectAllToggle}
          dropdownRef={dropdownRef}
          dropdownButtonRef={dropdownButtonRef}
          panelRef={panelRef}
          onFetchResources={handleFetchResources}
          hasPendingTypes={hasPendingTypes}
          isD2D={migrationMode === 'divisionToDivision'}
          includeDependencies={includeDependencies}
        />

        {migrationMode === 'divisionToDivision' && emptyDivisionTypes.length > 0 && (
          <div
            className={`mt-4 ${ui.page.sectionPadding} bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg`}
          >
            <div className={`flex items-start justify-between ${ui.gap.md}`}>
              <div className="min-w-0">
                <p className={`${ui.text.body} font-semibold text-amber-800 dark:text-amber-200`}>
                  {emptyDivisionTypes.length} selected type{emptyDivisionTypes.length === 1 ? '' : 's'} have no resources in {sourceDivisionName}
                </p>
                <p className={`${ui.text.xs} text-amber-700 dark:text-amber-300 mt-1 break-words`}>
                  These won&apos;t be migrated: {emptyDivisionTypes.join(', ')}
                </p>
              </div>
              <button
                type="button"
                onClick={handleRemoveEmptyDivisionTypes}
                className={`${ui.button.sm} shrink-0 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold`}
              >
                Remove empty types
              </button>
            </div>
          </div>
        )}

        {isFullSnapshotMode && (
          <div
            className={`mt-4 ${ui.page.sectionPadding} bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg`}
          >
            <div className={`flex items-center ${ui.gap.sm}`}>
              <svg
                className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <p className={`${ui.text.label} font-semibold text-blue-800 dark:text-blue-300`}>
                Full Export Mode Active
              </p>
            </div>
            <p className={`${ui.text.xs} text-blue-700 dark:text-blue-400 mt-2`}>
              {migrationMode === 'divisionToDivision'
                ? `${D2D_VISIBLE_RESOURCE_TYPES.length} division-aware resource types (division-scoped + org-wide) will be included with wildcard (*) selection`
                : `All ${RESOURCE_TYPES.length} resource types will be included with wildcard (*) selection`}
            </p>
          </div>
        )}
      </div>

      {/* Include Dependencies toggle — mirrors the Snapshot setup control. Defaults on
          (recommended) so selected resources' dependencies are auto-discovered and included;
          power users can uncheck to export only the explicitly selected resources. */}
      <div className={`flex items-center ${ui.gap.md} mt-6`}>
        <label className={`flex items-center ${ui.gap.sm} cursor-pointer`}>
          <input
            type="checkbox"
            checked={includeDependencies}
            onChange={(e) => setIncludeDependencies(e.target.checked)}
            className="w-4 h-4 text-blue-600 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 rounded focus:ring-blue-500 focus:ring-2"
          />
          <span
            className={`${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100`}
          >
            Include Dependencies
          </span>
        </label>
        <Tooltip
          content={
            <div className="space-y-3">
              <div>
                <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Checked (recommended)
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  Automatically discovers and migrates all resources your selected resources
                  depend on (e.g. queues, skills, schedules, wrap-up codes), so the target org
                  gets a complete, deployable configuration.
                </p>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
                <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Unchecked</p>
                <p className="text-gray-700 dark:text-gray-300">
                  Migrates only the explicitly selected resources. Use this only if all
                  dependencies already exist in the target org — missing dependencies will make
                  the migration fail or land incomplete.
                </p>
              </div>
            </div>
          }
          position="right"
          className={ui.text.formSectionLabel}
        />
      </div>

      {/* Enable Recurring Migrations */}
      {migrationSchedulingEnabled && (
        <ScheduleSection
          type="migration"
          isRecurringEnabled={isRecurringEnabled}
          onToggle={setIsRecurringEnabled}
          schedule={migrationSchedule}
          onScheduleChange={setMigrationSchedule}
          cronExpression={migrationCronExpression}
          scheduleDisplay={migrationScheduleDisplay ?? ''}
        />
      )}
      <RecurringMigrationConflictBanner
        active={conflictCheckEnabled}
        loading={conflictCheck.loading}
        hasConflict={conflictCheck.hasConflict}
        conflicts={conflictCheck.conflicts}
        targetOrgName={findOrganization(targetOrganization)?.name ?? targetOrganization}
        resolveOrgName={(storedName) => findOrganization(storedName)?.name ?? storedName}
        onViewDetails={handleViewConflictDetails}
      />
    </>
  );

  const rightHeader = (
    <>
      <SelectedResourcesTableHeader
        typeSearch={typeSearch}
        nameSearch={nameSearch}
        onSetTypeSearchChange={setTypeSearch}
        onSetNameSearchChange={setNameSearch}
      />
    </>
  );

  const rightBody = (
    <>
      <SelectedResourcesTableBody
        filteredResourcesByType={filteredResourcesByType}
        filteredResourceTypes={filteredResourceTypes}
        typeSearch={typeSearch}
        nameSearch={nameSearch}
        isFullSnapshotMode={isFullSnapshotMode}
        resourceTypeCounts={totalCountForAllTypes}
        loadingResources={loadingResources && isPrePopulatedForm}
        isD2D={migrationMode === 'divisionToDivision'}
      />
    </>
  );

  const rightExtraContent = (
    <SelectedResourcesTableButtons
      totalSelectedCount={totalSelectedCount}
      selectedResourceIds={selectedResourceIds}
      openConfirmModal={openConfirmModal}
    />
  );

  const handleNextClick = () => {
    setHasAttemptedSubmit(true);
    if (
      validateForm(
        migrationName,
        sourceOrganization,
        targetOrganization,
        totalSelectedCount,
        isFullSnapshotMode,
        migrationMode,
        sourceDivisionId,
        targetDivisionId
      )
    ) {
      handleRunMigrationClick();
    }
  };

  const buttons = (
    <div className={`flex flex-col items-end ${ui.gap.sm}`}>
      {hasAttemptedSubmit && validationError && validationError.length > 0 && (
        <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
          <ul className="list-disc pl-5 space-y-1">
            {validationError.map((err, idx) => (
              <li key={idx} className={`${ui.text.label} text-red-700 dark:text-red-300`}>
                {err}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex items-center space-x-3">
        {isPrePopulatedForm && (
          <button
            type="button"
            className={`rounded-lg ${ui.button.wide} font-semibold shadow transition bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100 cursor-pointer`}
            onClick={smartBack}
          >
            Back
          </button>
        )}
        <button
          className={`rounded-lg ${ui.button.wide} text-white font-semibold shadow transition
            ${isButtonEnabled
              ? 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 cursor-pointer'
              : 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60'
            }`}
          onClick={handleNextClick}
        >
          Next
        </button>
      </div>
    </div>
  );

  const migrationSetupGuidanceBanner = showGuidanceBanner ? (
    <div className="rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-4">
      <div className={`flex items-start justify-between ${ui.gap.md}`}>
        <div className={`flex items-start ${ui.gap.md}`}>
          {/* Info icon */}
          <div className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-blue-500 dark:bg-blue-600 flex items-center justify-center">
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v.01M12 12v6" />
            </svg>
          </div>
          <div className={`${ui.text.label} text-blue-800 dark:text-blue-200 space-y-2`}>
            <p className={`${ui.text.body} font-semibold text-blue-900 dark:text-blue-100`}>
              Getting started with Migration Setup
            </p>
            <div className="space-y-1.5">
              <div className="flex items-start gap-2">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-blue-900 dark:text-blue-100">
                    Start with organizations —{' '}
                  </span>
                  Select a source and target organization, then choose the resources you want to
                  move.
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-blue-900 dark:text-blue-100">
                    Dependencies are handled automatically —{' '}
                  </span>
                  All dependent Genesys objects will be automatically included to ensure a complete
                  migration.
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-blue-900 dark:text-blue-100">
                    You can refine later —{' '}
                  </span>
                  You&apos;ll be able to review and remove items during the transformation step if
                  desired.
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-blue-900 dark:text-blue-100">
                    Check the Selected Resources table —{' '}
                  </span>
                  Use it on the right to confirm your selections are accurate before continuing.
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-blue-900 dark:text-blue-100">
                    Green &ldquo;Select All&rdquo; indicator —{' '}
                  </span>
                  When lit, all resources of that type will be included, even those added after the
                  migration is queued.
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Dismiss button */}
        <button
          type="button"
          onClick={() => setShowGuidanceBanner(false)}
          title="Dismiss"
          className="flex-shrink-0 text-blue-400 dark:text-blue-500 hover:text-blue-600 dark:hover:text-blue-300 transition-colors"
          aria-label="Dismiss migration setup guidance"
        >
          <svg
            className={ui.svg.sm}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      {isEditMigrationPrefillLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="flex flex-col items-center gap-4 rounded-xl bg-white px-12 py-10 shadow-2xl dark:bg-[#0f1720] min-w-[320px]">
            {/* 12-spoke segmented activity-indicator spinner */}
            <svg
              viewBox="0 0 48 48"
              className="w-16 h-16"
              aria-hidden="true"
            >
              {Array.from({ length: 12 }).map((_, i) => (
                <rect
                  key={i}
                  x="22"
                  y="4"
                  width="4"
                  height="10"
                  rx="2"
                  fill="currentColor"
                  className="text-blue-500"
                  opacity={((i + 1) / 12).toFixed(2)}
                  transform={`rotate(${i * 30} 24 24)`}
                  style={{
                    animation: 'prefill-spoke-fade 1s linear infinite',
                    animationDelay: `${-(12 - i) * (1 / 12)}s`
                  }}
                />
              ))}
            </svg>
            <div className="flex flex-col items-center gap-1 text-center">
              <p className={`${ui.text.subTitle} font-semibold text-gray-900 dark:text-gray-100`}>
                Restoring your migration
              </p>
              <p className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>
                Filling in your previous settings from the failed migration and fetching resources from the source organization.
              </p>
            </div>
            <ul className={`${ui.text.xs} w-full space-y-1 text-gray-500 dark:text-gray-400`}>
              <li className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                Restoring migration name &amp; organizations
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                Loading resource types from the failed migration
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                Fetching &amp; re-selecting your resources — please wait
              </li>
            </ul>
          </div>
        </div>
      )}
      <SetupLayout
        title="Migration Details"
        titleAddon={
          <button
            type="button"
            onClick={() => setShowFAQs(true)}
            className="self-start mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-semibold shadow-sm hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:border-blue-400 dark:hover:border-blue-600 hover:shadow-md transition-all duration-150 group"
            aria-label="View RepliCloud FAQs"
          >
            <svg
              className="w-3.5 h-3.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-150"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z"
              />
            </svg>
            <span className="font-normal text-blue-500 dark:text-blue-400">Need help?</span>
            <span>FAQ</span>
          </button>
        }
        topBanner={migrationSetupGuidanceBanner}
        leftContent={leftContent}
        rightHeader={rightHeader}
        rightBody={rightBody}
        rightContentWrapperClass={tableScrollClass}
        rightExtraContent={rightExtraContent}
        selectedResourceTitle={
          isFullSnapshotMode ? 'Full Export (All Resources)' : selectedResourceTitle
        }
        selectedResourceCount={isFullSnapshotMode ? RESOURCE_TYPES.length : totalSelectedCount}
        buttonSection={buttons}
      />
      <Modal
        isOpen={modalState.variant === 'delete' ? confirmModalOpen : modalState.isOpen}
        variant={modalState.variant}
        title={
          modalState.variant === 'delete'
            ? confirmScope === 'all'
              ? 'Clear ALL Resource Type Selections'
              : 'Clear CURRENT Resource Type Selections'
            : 'Confirm Migration'
        }
        onClose={
          modalState.variant === 'delete'
            ? () => {
              logger.info('Modal closed', { type: 'delete', action: 'cancelled' });
              setConfirmModalOpen(false);
            }
            : () => {
              logger.info('Modal closed', { type: 'migration', action: 'cancelled' });
              handleModalClose();
            }
        }
        onConfirm={
          modalState.variant === 'delete'
            ? () => {
              if (confirmScope === 'all') {
                handleResetSelections('all');
                setIsFullSnapshotMode(false);
                setSelectedResourceTypes([]);
              } else {
                // Only clear the types the user just interacted with (the current selectedResourceTypes)
                handleResetSelections('current');
                setSelectedResourceTypes((prev) =>
                  prev.filter((type) => !selectedResourceTypes.includes(type))
                );
              }
              setConfirmModalOpen(false);
            }
            : handleRunConfirm
        }
        confirmLabel={
          modalState.variant === 'delete'
            ? 'Yes, clear'
            : migrationMode === 'divisionToDivision'
              ? 'Start Division-to-Division Migration'
              : 'Start Migration'
        }
        cancelLabel="Cancel"
        showConfirmButton={true}
        showIcon={true}
        confirmLoading={confirmLoading}
        confirmLoadingLabel="Starting migration..."
        showCloseButton
        setError={setError}
        error={error}
      >
        {
          ((): React.ReactNode => {
            if (modalState.variant === 'delete') {
              return (
                <>
                  <p className="text-sm text-center text-gray-700 dark:text-gray-300">
                    {confirmScope === 'all'
                      ? 'This will remove all saved selections across every resource type. This action cannot be undone.'
                      : 'This will clear selections only for the currently selected resource type.'}
                  </p>
                </>
              );
            }

            if (modalState.variant === 'runMigration') {
              // ── D2D pre-migration summary ────────────────────────────────────
              if (migrationMode === 'divisionToDivision') {
                // Full-snapshot mode selects every eligible type via wildcard, so no instances are
                // enumerated — count eligible TYPES. In manual mode count the actual resources the
                // user picked (instances), so the summary matches what will really migrate.
                const d2dScopedCount = isFullSnapshotMode
                  ? D2D_VISIBLE_RESOURCE_TYPES.filter((t) => DIVISION_SCOPED_RESOURCE_TYPES.includes(t)).length
                  : allSelectedResources.filter((r) => DIVISION_SCOPED_RESOURCE_TYPES.includes(r.type)).length;
                const d2dOrgWideCount = isFullSnapshotMode
                  ? D2D_VISIBLE_RESOURCE_TYPES.filter((t) => DIVISION_ORG_UNIQUE_RESOURCE_TYPES.includes(t)).length
                  : allSelectedResources.filter((r) => DIVISION_ORG_UNIQUE_RESOURCE_TYPES.includes(r.type)).length;
                const d2dCountNoun = isFullSnapshotMode ? 'resource type' : 'resource';
                return (
                  <div className="w-full max-w-2xl space-y-3">
                    {/* Migration name + source org */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg shrink-0">
                          <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400 mb-0.5 uppercase tracking-wider`}>Migration Name</p>
                          <p className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100 break-words leading-snug`}>{migrationName || '—'}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg shrink-0">
                          <svg className="w-4 h-4 text-teal-600 dark:text-teal-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400 mb-0.5 uppercase tracking-wider`}>Source Organization</p>
                          <p className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100 break-words leading-snug`}>
                            {sourceOrganization ? (() => {
                              const org = findOrganization(sourceOrganization);
                              return `${org?.name ?? sourceOrganization} - ${formatRegion(org?.region)}`;
                            })() : '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Division flow card */}
                    <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                      <div className="min-w-0">
                        <p className={`${ui.text.xs} font-medium text-blue-600 dark:text-blue-400 mb-0.5`}>Source Division</p>
                        <p className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100 break-words leading-snug`}>{sourceDivisionName || '—'}</p>
                      </div>
                      <svg className={`${ui.svg.sm} text-blue-400 shrink-0 self-center`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                      <div className="min-w-0">
                        <p className={`${ui.text.xs} font-medium text-blue-600 dark:text-blue-400 mb-0.5`}>Target Division</p>
                        <p className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100 break-words leading-snug`}>{targetDivisionName || '—'}</p>
                      </div>
                    </div>

                    {/* Recurring Schedule Display */}
                    {isRecurringEnabled && migrationScheduleDisplay && (
                      <div
                        className={`${ui.mb.title} bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-lg border border-emerald-200 dark:border-emerald-700 px-4 py-2.5`}
                      >
                        <div className={`flex items-center ${ui.gap.sm}`}>
                          <div className="p-2 bg-emerald-100 dark:bg-emerald-800/50 rounded-lg">
                            <svg
                              className={`${ui.svg.sm} text-emerald-600 dark:text-emerald-400`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div>
                            <div
                              className={`${ui.text.xs} font-medium text-emerald-700 dark:text-emerald-300 mb-1`}
                            >
                              Recurring Schedule
                            </div>
                            <div
                              className={`font-bold ${ui.text.label} text-emerald-900 dark:text-emerald-100`}
                            >
                              {migrationScheduleDisplay ?? 'Schedule not set'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Info note */}
                    <div className={`flex items-start ${ui.gap.sm} p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700`}>
                      <svg className={`${ui.svg.sm} text-gray-400 shrink-0 mt-0.5`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <p className={`${ui.text.xs} text-gray-600 dark:text-gray-400`}>
                        Division-scoped resources will be created fresh in the target division. Org-wide resources (e.g. Queues, Users) have org-wide unique names and will be reassigned to the target division — not duplicated.
                      </p>
                    </div>
                    {/* Resource counts */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative overflow-hidden rounded-xl border border-green-200 dark:border-green-700/60 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/10 p-5 shadow-sm">
                        <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-green-400 to-emerald-500" />
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          <div className="p-1.5 bg-green-100 dark:bg-green-800/60 rounded-md ring-1 ring-green-200 dark:ring-green-700">
                            <svg className={`${ui.svg.sm} text-green-600 dark:text-green-400`} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <p className={`${ui.text.xs} uppercase tracking-wider font-semibold text-green-700 dark:text-green-400`}>Will be copied</p>
                          {isFullSnapshotMode && (
                            <span className={`${ui.text.xs} font-semibold px-2 py-0.5 rounded-full bg-green-200 dark:bg-green-700 text-green-800 dark:text-green-200 whitespace-nowrap`}>
                              Full Snapshot
                            </span>
                          )}
                        </div>
                        <p className="text-5xl font-extrabold text-gray-900 dark:text-gray-100 leading-none mb-2 tabular-nums">{d2dScopedCount}</p>
                        <div className="h-px bg-green-200/70 dark:bg-green-700/40 my-2" />
                        <p className={`${ui.text.xs} text-gray-600 dark:text-gray-400 leading-snug`}>
                          {d2dCountNoun}{d2dScopedCount === 1 ? '' : 's'} will be copied to the target division
                        </p>
                      </div>
                      <div className="relative overflow-hidden rounded-xl border border-yellow-200 dark:border-yellow-700/60 bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/10 p-5 shadow-sm">
                        <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-yellow-400 to-amber-500" />
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          <div className="p-1.5 bg-yellow-100 dark:bg-yellow-800/60 rounded-md ring-1 ring-yellow-200 dark:ring-yellow-700">
                            <svg className={`${ui.svg.sm} text-yellow-600 dark:text-yellow-400`} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <p className={`${ui.text.xs} uppercase tracking-wider font-semibold text-yellow-700 dark:text-yellow-400`}>Will be reassigned</p>
                          {isFullSnapshotMode && (
                            <span className={`${ui.text.xs} font-semibold px-2 py-0.5 rounded-full bg-yellow-200 dark:bg-yellow-700 text-yellow-800 dark:text-yellow-200 whitespace-nowrap`}>
                              Full Snapshot
                            </span>
                          )}
                        </div>
                        <p className="text-5xl font-extrabold text-gray-900 dark:text-gray-100 leading-none mb-2 tabular-nums">{d2dOrgWideCount}</p>
                        <div className="h-px bg-yellow-200/70 dark:bg-yellow-700/40 my-2" />
                        <p className={`${ui.text.xs} text-gray-600 dark:text-gray-400 leading-snug`}>
                          {d2dCountNoun}{d2dOrgWideCount === 1 ? '' : 's'} with org-wide unique names — will be reassigned to the target division, not duplicated
                        </p>
                      </div>
                    </div>
                    {/* Selected resources table */}
                    {displayedResources.length > 0 && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <SelectedResourcesTableHeader
                          typeSearch={typeSearch}
                          nameSearch={nameSearch}
                          onSetTypeSearchChange={setTypeSearch}
                          onSetNameSearchChange={setNameSearch}
                        />
                        <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
                          <SelectedResourcesTableBody
                            filteredResourcesByType={filteredResourcesByType}
                            filteredResourceTypes={filteredResourceTypes}
                            typeSearch={typeSearch}
                            nameSearch={nameSearch}
                            isFullSnapshotMode={isFullSnapshotMode}
                            resourceTypeCounts={totalCountForAllTypes}
                            isD2D={true}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              // ── Standard Org-to-Org summary (existing) ───────────────────────
              return (
                <div className="w-full max-w-2xl space-y-3">
                  {/* Summary Card */}
                  <div className="space-y-3">
                    {/* Migration Name + Export Name (paired) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg shrink-0">
                          <svg
                            className="w-4 h-4 text-orange-600 dark:text-orange-400"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                            />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider`}>
                            Migration Name
                          </div>
                          <div className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100 break-words leading-snug`}>
                            {migrationName || '[Name]'}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg shrink-0">
                          <svg
                            className="w-4 h-4 text-purple-600 dark:text-purple-400"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider`}>
                            Export Name
                          </div>
                          <div className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100 break-words leading-snug`}>
                            {snapshotName || '[Name]'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Source → Target Org flow card */}
                    <div className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800/50 dark:to-gray-800/30 p-4 shadow-sm">
                      <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-4">
                        {/* Source */}
                        <div className="flex items-start gap-3 min-w-0">
                          <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg shrink-0">
                            <svg className="w-4 h-4 text-teal-600 dark:text-teal-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`${ui.text.xs} font-semibold text-teal-700 dark:text-teal-400 mb-1 uppercase tracking-wider`}>
                              Source Org
                            </div>
                            <div className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100 break-words leading-snug`}>
                              {sourceOrganization ? (() => {
                                const org = findOrganization(sourceOrganization);
                                return `${org?.name ?? sourceOrganization} - ${formatRegion(org?.region)}`;
                              })() : '[Org]'}
                            </div>
                          </div>
                        </div>

                        {/* Arrow */}
                        <div className="flex items-center justify-center self-center">
                          <svg className="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </div>

                        {/* Target */}
                        <div className="flex items-start gap-3 min-w-0">
                          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg shrink-0">
                            <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`${ui.text.xs} font-semibold text-indigo-700 dark:text-indigo-400 mb-1 uppercase tracking-wider`}>
                              Target Org
                            </div>
                            <div className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100 break-words leading-snug`}>
                              {targetOrganization ? (() => {
                                const org = findOrganization(targetOrganization);
                                return `${org?.name ?? targetOrganization} - ${formatRegion(org?.region)}`;
                              })() : '[Org]'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Recurring Schedule Display */}
                    {isRecurringEnabled && migrationScheduleDisplay && (
                      <div
                        className={`${ui.mb.title} bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-lg border border-emerald-200 dark:border-emerald-700 px-4 py-2.5`}
                      >
                        <div className={`flex items-center ${ui.gap.sm}`}>
                          <div className="p-2 bg-emerald-100 dark:bg-emerald-800/50 rounded-lg">
                            <svg
                              className={`${ui.svg.sm} text-emerald-600 dark:text-emerald-400`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div>
                            <div
                              className={`${ui.text.xs} font-medium text-emerald-700 dark:text-emerald-300 mb-1`}
                            >
                              Recurring Schedule
                            </div>
                            <div
                              className={`font-bold ${ui.text.label} text-emerald-900 dark:text-emerald-100`}
                            >
                              {migrationScheduleDisplay ?? 'Schedule not set'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Resources count tile (D2D-style) */}
                    <div className="relative overflow-hidden rounded-xl border border-blue-200 dark:border-blue-700/60 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/10 p-5 shadow-sm">
                      <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-blue-400 to-indigo-500" />
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-1.5 bg-blue-100 dark:bg-blue-800/60 rounded-md ring-1 ring-blue-200 dark:ring-blue-700">
                          <svg className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`} fill="currentColor" viewBox="0 0 20 20">
                            <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                          </svg>
                        </div>
                        <p className={`${ui.text.xs} uppercase tracking-wider font-semibold text-blue-700 dark:text-blue-400`}>
                          {isFullSnapshotMode ? 'Resource Types' : 'Resources'}
                        </p>
                        {isFullSnapshotMode && (
                          <span className={`${ui.text.xs} font-semibold px-2 py-0.5 rounded-full bg-blue-200 dark:bg-blue-700 text-blue-800 dark:text-blue-200`}>
                            Full Snapshot
                          </span>
                        )}
                      </div>
                      <p className="text-5xl font-extrabold text-gray-900 dark:text-gray-100 leading-none mb-2 tabular-nums">
                        {isFullSnapshotMode ? RESOURCE_TYPES.length : allSelectedResources.length}
                      </p>
                      <div className="h-px bg-blue-200/70 dark:bg-blue-700/40 my-2" />
                      <p className={`${ui.text.xs} text-gray-600 dark:text-gray-400 leading-snug`}>
                        {isFullSnapshotMode
                          ? 'resource types will be included via full snapshot (wildcard)'
                          : (allSelectedResources.length === 1
                            ? 'resource will be migrated to the target organization'
                            : 'resources will be migrated to the target organization')}
                      </p>
                    </div>
                  </div>

                  {/* Resources by Type - Accordion */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <SelectedResourcesTableHeader
                      typeSearch={typeSearch}
                      nameSearch={nameSearch}
                      onSetTypeSearchChange={setTypeSearch}
                      onSetNameSearchChange={setNameSearch}
                    />
                    <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
                      <SelectedResourcesTableBody
                        filteredResourcesByType={filteredResourcesByType}
                        filteredResourceTypes={filteredResourceTypes}
                        typeSearch={typeSearch}
                        nameSearch={nameSearch}
                        isFullSnapshotMode={isFullSnapshotMode}
                        resourceTypeCounts={totalCountForAllTypes}
                      />
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })() as React.ReactNode
        }
      </Modal>
      <Modal
        isOpen={conflictDetailsId !== null}
        variant={conflictDetailsVariant}
        title={conflictDetailsJob?.name ?? 'Migration Details'}
        onClose={() => setConflictDetailsId(null)}
        cancelLabel="Close"
        showConfirmButton={false}
        showCloseButton={true}
      >
        {conflictDetailsLoading && !conflictDetailsJob ? (
          <div className="flex items-center justify-center gap-3 py-10">
            <Spinner />
            <span className={ui.text.body}>Loading migration details…</span>
          </div>
        ) : conflictDetailsJob ? (
          conflictDetailsVariant === 'attention' ? (
            <AttentionModalContent job={conflictDetailsJob} />
          ) : (
            <ViewModalContent job={conflictDetailsJob} />
          )
        ) : (
          <div className="py-10 text-center">
            <p className={ui.text.body}>Migration details could not be found.</p>
          </div>
        )}
      </Modal>
      <RepliCloudFAQsModal
        isOpen={showFAQs}
        onClose={() => setShowFAQs(false)}
        onBack={() => setShowFAQs(false)}
      />
    </>
  );
};

export default MigrationSetup;
